import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import config from '../../config/config';
import DashboardLayout from '../shared/layout/DashboardLayout';
import { getKLMenuItems } from '../../utils/menuItems';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { UploadCloud, Info, CheckCircle, AlertTriangle, Send, FilePlus, File as FileIcon, ChevronDown, ChevronUp, X, HelpCircle, Download, Save, FileCode, Edit3 } from 'lucide-react';
import MetadataEditor from '../../components/MetadataEditor';

import '../../styles/UploadPage.css';
import '../../styles/RekapDataDetails.css';

const renameFile = (file) => {
  const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
  const fileNameWithoutExtension = file.name.substring(0, file.name.lastIndexOf('.'));
  const sanitizedName = fileNameWithoutExtension
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '');
  const newFileName = `${sanitizedName}${fileExtension}`;
  return new File([file], newFileName, { type: file.type });
};

const ErrorModal = ({ isOpen, onClose, title, message }) => {
  if (!isOpen) return null;

  return (
    <div className="survey-modal-overlay">
      <div className="survey-modal alert-modal">
        <div className="survey-header alert-modal-header">
          <div className="survey-header-content">
            <AlertTriangle size={24} />
            <div>
              <h3>{title}</h3>
            </div>
          </div>
          <button onClick={onClose} className="survey-close-btn"><X size={20} /></button>
        </div>
        <div className="survey-form modal-form-body">
          <p className="modal-text">{message}</p>
          <div className="form-actions modal-actions">
            <button onClick={onClose} className="btn-submit btn-mengerti">
              Mengerti
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="survey-modal-overlay">
      <div className="survey-modal alert-modal">
        <div className="survey-header">
          <div className="survey-header-content">
            <HelpCircle size={24} />
            <div>
              <h3>Konfirmasi Pengajuan</h3>
            </div>
          </div>
        </div>
        <div className="survey-form modal-form-body">
          <p className="modal-text">
            Apakah Anda yakin ingin mengirimkan data ini? Pastikan semua informasi dan dokumen yang diunggah sudah benar.
          </p>
          <div className="form-actions modal-actions">
            <button onClick={onClose} className="btn-cancel" disabled={loading}>
              Batal
            </button>
            <button onClick={onConfirm} className="btn-confirm" disabled={loading}>
              {loading ? 'Mengirim...' : 'Ya, Kirim'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UploadPage = () => {
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [username, setUsername] = useState(null);
  const [userId, setUserId] = useState(null);
  const [kl, setKl] = useState(null);
  const [kodekl, setKodeKl] = useState(null);
  const [fileTypes, setFileTypes] = useState([]);
  const [selectedIgt, setSelectedIgt] = useState('');
  const [jenisKsp, setJenisKsp] = useState('');
  const [kode, setKode] = useState('');
  const [kepkaData, setKepkaData] = useState(null);

  const uploadFields = [
    { id: 'file', label: 'Peta', accept: '.gdb,.shp,.geotiff,.zip,.rar', required: true, document_type: 'peta', isFile: true },
    { id: 'file1', label: 'Struktur Data', accept: '.pdf,.zip', required: false, document_type: 'struktur_data', isFile: true },
    { id: 'file2', label: 'Mekanisme dan Tata Kerja', accept: '.pdf,.zip', required: false, document_type: 'takel', isFile: true },
    { id: 'file3', label: 'Dokumen Penetapan', accept: '.zip', required: false, document_type: 'dokumen', isFile: true },
    { id: 'file4', label: 'Metadata', accept: '.xml,.zip', required: false, document_type: 'metadata', isFile: true },
    { id: 'file5', label: 'Simbology', accept: '.zip', required: false, document_type: 'simbology', isFile: true },
    { id: 'file6', label: 'Role Akses', accept: '.pdf,.zip', required: false, document_type: 'role', isFile: true },
    { id: 'file8', label: 'Kontrol Kualitas', accept: '.pdf,.zip', required: false, document_type: 'kualitas', isFile: true }
  ];
  const [cakupan, setCakupan] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileNames, setFileNames] = useState({});
  const [fileErrors, setFileErrors] = useState({});

  const [capaianList, setCapaianList] = useState([{ tahun: new Date().getFullYear(), nilai: '', target: '', satuan: '' }]);
  const [isManualInput, setIsManualInput] = useState(false);
  const [futureYearsTarget, setFutureYearsTarget] = useState([]);
  const [noSK, setNoSK] = useState('');
  const [isLainnya, setIsLainnya] = useState(false);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [errorModalTitle, setErrorModalTitle] = useState('');

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [downloadLoading, setDownloadLoading] = useState({});

  const [showMetadataEditor, setShowMetadataEditor] = useState(false);
  const [metadataFile, setMetadataFile] = useState(null);

  const [checklistData, setChecklistData] = useState([]);
  const [checklistInputs, setChecklistInputs] = useState({});
  const [expandedChecklists, setExpandedChecklists] = useState({});
  const [isDocsExpanded, setIsDocsExpanded] = useState(false);

  const [latestDocuments, setLatestDocuments] = useState({
    struktur_data: null,
    takel: null,
    simbology: null,
    role: null
  });
  const [syncedDocuments, setSyncedDocuments] = useState({
    struktur_data: null,
    takel: null,
    simbology: null,
    role: null
  });

  const allowedExtensions = {
    file: ['.gdb', '.shp', '.geotiff', '.zip', '.rar'],
    file1: ['.pdf', '.zip'],
    file2: ['.pdf', '.zip'],
    file3: ['.zip'],
    file4: ['.xml', '.zip'],
    file5: ['.zip'],
    file6: ['.pdf', '.zip'],
    file8: ['.pdf', '.zip']
  };

  const templateMapping = {
    file2: { endpoint: 'template-mekanisme-tata-kerja', fileName: 'Template_Mekanisme_Tata_Kerja.pdf' },
    file3: { endpoint: 'template-dokumen-penetapan', fileName: 'Template_Dokumen_Penetapan.zip' },
    file1: { endpoint: 'template-kamus-data', fileName: 'Template_Kamus_Data.pdf' },
    file4: { endpoint: 'template-metadata', fileName: 'Template_Metadata.xml' },
    file6: { endpoint: 'template-role-akses', fileName: 'Template_Role_Akses.pdf' },
    file5: { endpoint: 'template-simbology', fileName: 'Template_Simbology.zip' }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    try {
      const decodedToken = jwtDecode(token);
      setUsername(decodedToken.username);
      setUserId(decodedToken.userId);
      setKl(decodedToken.kl);
      setKodeKl(decodedToken.kodekl);
    } catch (error) {
      console.error("Error decoding token:", error);
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    if (!kodekl) return;
    const fetchFileTypes = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${config.API_URL}/upload/igt`,
          { kodekl },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setFileTypes(response.data);
      } catch (error) {
        console.error("Error fetching file types:", error);
      }
    };
    fetchFileTypes();
  }, [kodekl]);

  useEffect(() => {
    const fetchChecklist = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${config.API_URL}/api/checklist/all`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.data.success) {
          const activeChecklist = response.data.checklist.filter(item => item.is_active);
          setChecklistData(activeChecklist);
        }
      } catch (error) {
        console.error("Error fetching checklist:", error);
      }
    };
    fetchChecklist();
  }, []);

  useEffect(() => {
    if (!userId) return;

    const loadDraft = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${config.API_URL}/upload/draft/${userId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        if (response.data.success && response.data.data) {
          const draft = response.data.data;
          setSelectedIgt(draft.selectedIgt || '');
          setJenisKsp(draft.jenisKsp || '');
          setKode(draft.kode || '');
          setCakupan(draft.cakupan || '');
          setCapaianList(draft.capaianList || [{ tahun: new Date().getFullYear(), nilai: '', target: '', satuan: '' }]);
          setKepkaData(draft.kepkaId ? { id: draft.kepkaId } : null);
          setIsManualInput(draft.isManualInput || false);
          setChecklistInputs(draft.checklistInputs || {});
          setFutureYearsTarget(draft.futureYearsTarget || []);
          setSyncedDocuments(draft.syncedDocuments || {
            struktur_data: null,
            takel: null,
            simbology: null,
            role: null
          });

          toast.info(`📋 Draft dimuat (${new Date(response.data.savedAt).toLocaleString('id-ID')}). Silakan upload file kembali.`, { autoClose: 5000 });
        }
      } catch (error) {
      }
    };

    loadDraft();
  }, [userId]);

  // Sync isLainnya when selectedIgt or fileTypes changes
  useEffect(() => {
    if (selectedIgt && fileTypes.length > 0) {
      const selectedType = fileTypes.find(type => type.nama_igt === selectedIgt);
      const isLainnyaIgt = !selectedType || !selectedType.jenis_sk || selectedType.jenis_sk === 'Lainnya';
      setIsLainnya(isLainnyaIgt);
    } else {
      setIsLainnya(false);
    }
  }, [selectedIgt, fileTypes]);

  useEffect(() => {
    if (!selectedIgt || !userId) return;

    const fetchLatestDocuments = async () => {
      try {
        const token = localStorage.getItem('token');
        const documentTypes = ['struktur_data', 'takel', 'simbology', 'role'];

        const latestDocsData = {
          struktur_data: null,
          takel: null,
          simbology: null,
          role: null
        };

        for (const docType of documentTypes) {
          try {
            const response = await axios.get(
              `${config.API_URL}/data/latest-document?nama_igt=${encodeURIComponent(selectedIgt)}&userId=${userId}&document_type=${docType}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (response.data && response.data.document_url) {
              latestDocsData[docType] = response.data;
            }
          } catch (error) {
            console.error(`Error fetching latest ${docType} document:`, error);
          }
        }

        setLatestDocuments(latestDocsData);
      } catch (error) {
        console.error("Error fetching latest documents:", error);
      }
    };

    fetchLatestDocuments();
  }, [selectedIgt, userId]);

  // Handle synchronize document
  const handleSyncDocument = async (documentType) => {
    const fileIdMapping = {
      struktur_data: 'file1',
      takel: 'file2',
      simbology: 'file5',
      role: 'file6'
    };

    const fileId = fileIdMapping[documentType];
    const latestDoc = latestDocuments[documentType];

    if (!latestDoc) return;

    setSyncedDocuments(prev => ({ ...prev, [documentType]: latestDoc }));

    setFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[fileId];
      return newFiles;
    });

    setFileNames(prev => ({
      ...prev,
      [fileId]: `🔄 ${latestDoc.filename} (tersinkronisasi dari ${latestDoc.no_tiket})`
    }));

    try {
      const token = localStorage.getItem('token');

      const checklistResponse = await axios.get(
        `${config.API_URL}/api/checklist/${documentType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (checklistResponse.data && checklistResponse.data.checklist && checklistResponse.data.checklist.length > 0) {
        const dataResponse = await axios.get(
          `${config.API_URL}/api/checklist/data/${latestDoc.no_tiket}/${documentType}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (dataResponse.data && dataResponse.data.checklistData && dataResponse.data.checklistData.items) {
          const syncedItems = dataResponse.data.checklistData.items;

          setChecklistInputs(prev => {
            const newInputs = { ...prev };
            Object.keys(syncedItems).forEach(itemId => {
              if (syncedItems[itemId].userInput) {
                newInputs[itemId] = syncedItems[itemId].userInput;
              }
            });
            return newInputs;
          });

          console.log(`✅ Checklist data synced for ${documentType}`);
        }
      } else {
        console.log(`ℹ️ No checklist template found for ${documentType}`);
      }
    } catch (error) {
      console.log(`ℹ️ No checklist data found for ${documentType}:`, error.message);
    }

    const docLabels = {
      struktur_data: 'Struktur Data',
      takel: 'Mekanisme dan Tata Kerja',
      simbology: 'Simbology',
      role: 'Role Akses'
    };

    toast.success(`${docLabels[documentType]} berhasil disinkronisasi dari tiket ${latestDoc.no_tiket}!`);
  };

  const handleUnsyncDocument = (documentType) => {
    const fileIdMapping = {
      struktur_data: 'file1',
      takel: 'file2',
      simbology: 'file5',
      role: 'file6'
    };

    const fileId = fileIdMapping[documentType];

    setSyncedDocuments(prev => ({ ...prev, [documentType]: null }));

    setFileNames(prev => {
      const newNames = { ...prev };
      delete newNames[fileId];
      return newNames;
    });

    const docLabels = {
      struktur_data: 'Struktur Data',
      takel: 'Mekanisme dan Tata Kerja',
      simbology: 'Simbology',
      role: 'Role Akses'
    };

    toast.info(`Sinkronisasi ${docLabels[documentType]} dibatalkan.`);
  };

  const downloadTemplate = async (templateType) => {
    const templateInfo = templateMapping[templateType];
    if (!templateInfo) {
      toast.error('Template tidak tersedia untuk dokumen ini.');
      return;
    }

    setDownloadLoading(prev => ({ ...prev, [templateType]: true }));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_URL}/template/download/${templateInfo.endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', templateInfo.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Template ${templateInfo.fileName} berhasil diunduh.`);
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error(`Gagal mengunduh template ${templateInfo.fileName}.`);
    } finally {
      setDownloadLoading(prev => ({ ...prev, [templateType]: false }));
    }
  };

  const handleCakupanChange = (e) => setCakupan(e.target.value);

  // Fungsi untuk menambah input capaian tahun
  const addCapaianTahun = () => {
    // Find next available year (not already used)
    const currentYear = new Date().getFullYear();
    const usedYears = capaianList.map(item => parseInt(item.tahun));

    // Find the next year that hasn't been used
    let nextYear = currentYear;
    for (let year = currentYear; year <= currentYear + 15; year++) {
      if (!usedYears.includes(year)) {
        nextYear = year;
        break;
      }
    }

    setCapaianList([...capaianList, { tahun: nextYear, nilai: '', target: '', satuan: '' }]);
  };

  // Fungsi untuk menghapus input capaian tahun
  const removeCapaianTahun = (index) => {
    if (capaianList.length > 1) {
      const newList = capaianList.filter((_, i) => i !== index);
      setCapaianList(newList);
    }
  };

  // Fungsi untuk update capaian tahun
  const updateCapaianTahun = (index, field, value) => {
    const newList = [...capaianList];
    newList[index][field] = value;

    // Jika tahun diubah dan kita punya kepkaData (bukan manual input)
    if (field === 'tahun' && !isManualInput && kepkaData && kepkaData.target) {
      newList[index].target = kepkaData.target[value] || '';
      newList[index].satuan = kepkaData.satuan || '';

      const capaian = parseFloat(newList[index].nilai) || 0;
      const target = parseFloat(newList[index].target) || 0;
      if (target > 0) {
        const persentase = (capaian / target) * 100;
        newList[index].persentase = Math.min(100, persentase).toFixed(2);
      } else {
        newList[index].persentase = 0;
      }
    }

    // Auto-calculate persentase jika nilai atau target berubah
    if (field === 'nilai' || field === 'target') {
      const capaian = parseFloat(newList[index].nilai) || 0;
      const target = parseFloat(newList[index].target) || 0;

      if (target > 0) {
        const persentase = (capaian / target) * 100;
        // Max 100% - jika capaian melebihi target, tetap 100%
        newList[index].persentase = Math.min(100, persentase).toFixed(2);
      } else {
        newList[index].persentase = 0;
      }
    }

    setCapaianList(newList);
  };

  // Calculate percentage automatically from capaian input
  const calculatePersentaseCapaian = (nilai, target) => {
    if (!nilai || !target || target <= 0) return 0;
    const persentase = (parseFloat(nilai) / parseFloat(target)) * 100;
    // Max 100% - jika capaian melebihi target, tetap 100%
    return Math.min(100, Math.round(persentase * 100) / 100);
  };

  const isValidFileExtension = (fileName, fileId) => {
    if (!fileName || !allowedExtensions[fileId]) return false;
    const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
    return allowedExtensions[fileId].includes(extension);
  };

  const getFieldName = (id) => {
    const field = formInputs.find(input => input.id === id);
    return field ? field.label : 'Field tidak dikenal';
  };

  const handleFileChange = (e) => {
    const { id, files: selectedFiles } = e.target;
    if (selectedFiles.length === 0) return;
    const selectedFile = selectedFiles[0];

    if (!isValidFileExtension(selectedFile.name, id)) {
      const fieldName = getFieldName(id);
      setErrorModalTitle('Format File Tidak Sesuai');
      setErrorModalMessage(`File untuk "${fieldName}" tidak sesuai. Format yang diizinkan adalah: ${allowedExtensions[id].join(', ')}`);
      setShowErrorModal(true);

      setFileErrors(prev => ({ ...prev, [id]: `Format tidak diizinkan.` }));
      e.target.value = '';
      return;
    }

    setFileErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });

    const sanitizedFile = renameFile(selectedFile);
    setFiles(prev => ({ ...prev, [id]: sanitizedFile }));
    setFileNames(prev => ({ ...prev, [id]: sanitizedFile.name }));

    // If metadata file (file4) is XML, allow editing with MetadataEditor
    if (id === 'file4' && sanitizedFile.name.toLowerCase().endsWith('.xml')) {
      setMetadataFile(sanitizedFile);
    }
  };

  // Handle opening Metadata Editor
  const handleOpenMetadataEditor = () => {
    if (metadataFile) {
      setShowMetadataEditor(true);
    } else {
      toast.warning('Pilih file metadata XML terlebih dahulu');
    }
  };

  // Handle saving metadata from editor
  const handleSaveMetadata = async (xmlContent, values) => {
    try {
      // Create a new file from the edited XML content
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      const fileName = metadataFile?.name || 'metadata.xml';
      const newFile = new File([blob], fileName, { type: 'application/xml' });

      setFiles(prev => ({ ...prev, file4: newFile }));
      setFileNames(prev => ({ ...prev, file4: `✅ ${fileName} (diedit)` }));
      setMetadataFile(newFile);

      toast.success('Metadata berhasil diperbarui!');
    } catch (error) {
      console.error('Error saving metadata:', error);
      toast.error('Gagal menyimpan metadata');
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();

    if (Object.keys(fileErrors).length > 0) {
      toast.error('Masih ada format file yang salah. Mohon perbaiki.');
      return;
    }

    const isRenaksi = kepkaData?.jenis === 'Renaksi';
    const requiredFiles = isRenaksi ? [] : uploadFields.filter(field => field.required && field.isFile);
    const missingFiles = requiredFiles.filter(field => !files[field.id]);

    if (missingFiles.length > 0 || !selectedIgt || !cakupan) {
      setErrorModalTitle('Data Belum Lengkap');
      setErrorModalMessage('Harap lengkapi semua field yang wajib diisi (ditandai dengan *) sebelum melanjutkan.');
      setShowErrorModal(true);
      return;
    }

    // Validasi capaian
    if (!isLainnya) {
      const hasEmptyCapaian = capaianList.some(item => !item.tahun || !item.nilai);
      if (hasEmptyCapaian) {
        setErrorModalTitle('Data Capaian Belum Lengkap');
        setErrorModalMessage('Harap lengkapi semua tahun dan nilai capaian.');
        setShowErrorModal(true);
        return;
      }

      // Validasi untuk manual input (kode 3)
      if (isManualInput) {
        const hasEmptyTargetOrSatuan = capaianList.some(item => !item.target || !item.satuan);
        if (hasEmptyTargetOrSatuan) {
          setErrorModalTitle('Data Target Belum Lengkap');
          setErrorModalMessage('Harap lengkapi target dan satuan untuk semua tahun.');
          setShowErrorModal(true);
          return;
        }
      }
    }

    setShowConfirmModal(true);
  };

  // Save draft function
  const handleSaveDraft = async () => {
    try {
      const token = localStorage.getItem('token');
      const draftData = {
        userId,
        selectedIgt,
        jenisKsp,
        kode,
        cakupan,
        capaianList,
        kepkaId: kepkaData?.id || null,
        isManualInput,
        checklistInputs,
        futureYearsTarget,
        syncedDocuments
      };

      await axios.post(`${config.API_URL}/upload/draft`, draftData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('💾 Draft berhasil disimpan!');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Gagal menyimpan draft');
    }
  };

  const executeUpload = async () => {
    setShowConfirmModal(false);
    setLoading(true);

    const formData = new FormData();

    // Append files yang ada
    Object.keys(files).forEach(key => {
      if (files[key]) {
        formData.append(key, files[key]);
      }
    });

    // Handle synchronized documents
    const docTypeMapping = {
      struktur_data: 'file1',
      takel: 'file2',
      simbology: 'file5',
      role: 'file6'
    };

    Object.entries(syncedDocuments).forEach(([docType, docData]) => {
      const fileId = docTypeMapping[docType];
      if (docData && !files[fileId]) {
        formData.append(`${docType}FromTicket`, docData.document_url);
        formData.append(`sync${docType.charAt(0).toUpperCase() + docType.slice(1)}Ticket`, docData.no_tiket);
      }
    });

    const isRenaksiJenis = kepkaData?.jenis === 'Renaksi';

    if (!isRenaksiJenis && !files.file) {
      toast.error('File peta wajib diupload');
      setLoading(false);
      return;
    }

    const no_tiket = `${kode || 'UNKNOWN'}-${Date.now()}`;
    formData.append('fileType', selectedIgt);
    formData.append('no_tiket', no_tiket);
    formData.append('kl', kl);
    formData.append('kode_igt', kode);
    formData.append('userId', userId);
    formData.append('status', 'Belum Diverifikasi');
    formData.append('jenis', jenisKsp);
    formData.append('jenisKepka', kepkaData?.jenis || '');
    formData.append('file7', cakupan);
    formData.append('capaianData', JSON.stringify(isLainnya ? [] : capaianList));
    formData.append('kepkaId', kepkaData ? kepkaData.id : '');
    formData.append('isManualInput', isManualInput);
    formData.append('noSK', noSK);
    formData.append('checklistData', JSON.stringify(checklistInputs));
    formData.append('futureYearsTarget', JSON.stringify(isLainnya ? [] : futureYearsTarget));
    formData.append('isLainnya', isLainnya);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${config.API_URL}/upload`, formData, {
        headers: { 'Authorization': `Bearer ${token}` },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      setUploadProgress(100);

      // Auto-delete draft after successful upload
      try {
        await axios.delete(`${config.API_URL}/upload/draft/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (draftError) {
        // Silently ignore if draft doesn't exist
      }

      toast.success('File berhasil diupload!', { autoClose: 3000 });
      navigate(`/kl/success?no_tiket=${no_tiket}`);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Gagal mengupload file.');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleIgtChange = async (e) => {
    const selected = e.target.value;
    setSelectedIgt(selected);
    const selectedType = fileTypes.find(type => type.nama_igt === selected);
    setJenisKsp(selectedType ? selectedType.jenis_ksp : '');
    setKode(selectedType ? selectedType.kode_igt : '');

    // Reset nomor SK
    setNoSK('');

    // Reset document synchronization states
    setLatestDocuments({
      struktur_data: null,
      takel: null,
      simbology: null,
      role: null
    });
    setSyncedDocuments({
      struktur_data: null,
      takel: null,
      simbology: null,
      role: null
    });
    // Clear synchronized document files
    setFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles.file1; // struktur_data
      delete newFiles.file2; // takel
      delete newFiles.file5; // simbology
      delete newFiles.file6; // role
      return newFiles;
    });
    setFileNames(prev => {
      const newNames = { ...prev };
      delete newNames.file1;
      delete newNames.file2;
      delete newNames.file5;
      delete newNames.file6;
      return newNames;
    });

    // Reset fields if it is "Lainnya" category
    const isLainnyaIgt = !selectedType || !selectedType.jenis_sk || selectedType.jenis_sk === 'Lainnya';
    setIsLainnya(isLainnyaIgt);

    if (isLainnyaIgt) {
      setKepkaData(null);
      setIsManualInput(false);
      setCapaianList([]);
      return;
    }

    // Fetch kepka data berdasarkan kode_igt
    if (selectedType && selectedType.kode_igt) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${config.API_URL}/api/management/kepka/kode/${selectedType.kode_igt}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        // Check if kepka data exists
        if (response.data.success && response.data.data) {
          const kepka = response.data.data;
          setKepkaData(kepka);

          // Jika kepka sudah punya target dan satuan, tidak perlu manual input
          const hasTargetData = kepka.target && kepka.satuan;
          setIsManualInput(!hasTargetData);

          if (hasTargetData) {
            const currentYear = new Date().getFullYear();
            const targetData = kepka.target;

            // Hanya tampilkan capaian tahun ini dengan target dari kepka
            setCapaianList([{
              tahun: currentYear,
              nilai: '',
              target: targetData[currentYear] || '',
              satuan: kepka.satuan
            }]);
          } else {
            // Reset untuk manual input jika tidak ada target di kepka
            setCapaianList([{ tahun: new Date().getFullYear(), nilai: '', target: '', satuan: '' }]);
          }
        } else {
          setKepkaData(null);
          setIsManualInput(true);
          setCapaianList([{ tahun: new Date().getFullYear(), nilai: '', target: '', satuan: '' }]);
        }
      } catch (error) {
        console.error('Error fetching kepka data:', error);
        setKepkaData(null);
        setIsManualInput(true);
        setCapaianList([{ tahun: new Date().getFullYear(), nilai: '', target: '', satuan: '' }]);
      }
    }
  };

  const formInputs = [
    { id: 'file', label: 'Data Peta', accept: '.gdb, .shp, .geotiff, .zip, .rar', required: true },
    { id: 'file1', label: 'Kamus Data / Struktur Data', accept: '.pdf, .zip' },
    { id: 'file2', label: 'Mekanisme dan Tata Kerja', accept: '.pdf, .zip' },
    { id: 'file3', label: 'Dokumen Penetapan', accept: '.zip' },
    { id: 'file4', label: 'Metadata', accept: '.xml, .zip' },
    { id: 'file5', label: 'Simbology', accept: '.zip' },
    { id: 'file6', label: 'Role Akses', accept: '.pdf, .zip' },
    { id: 'file8', label: 'Kontrol Kualitas', accept: '.pdf, .zip' }
  ];

  return (
    <DashboardLayout menuItems={getKLMenuItems()} userRole="Pemerintah">
      <div className="container mt-4">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/kl">Dashboard</a></li>
            <li className="breadcrumb-item active" aria-current="page">Upload Data</li>
          </ol>
        </nav>
        <ToastContainer position="top-center" autoClose={3000} theme="colored" />

        <ErrorModal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          title={errorModalTitle}
          message={errorModalMessage}
        />
        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={executeUpload}
          loading={loading}
        />

        <MetadataEditor
          isOpen={showMetadataEditor}
          onClose={() => setShowMetadataEditor(false)}
          metadataFile={metadataFile}
          onSave={handleSaveMetadata}
          readOnly={false}
          mode="edit"
        />

        <div className="upload-page-wrapper">
          <form onSubmit={handleFileUpload}>
            <div className="rekap-header-card">
              <div className="header-content">
                <h2 className="rekap-title">Formulir Upload IGT</h2>
                <p className="welcome-text welcome-text-white">
                  Silakan lengkapi informasi dan unggah dokumen yang diperlukan di bawah ini.
                </p>
              </div>
            </div>

            <div className="files-section">
              <h3 className="section-title"><Info size={22} /> Informasi Dasar</h3>
              <div className="form-card">
                <div className="form-group">
                  <label className="form-label" htmlFor="igt-select">Nama IGT <span className="required">*</span></label>
                  <select id="igt-select" className="form-input" required value={selectedIgt} onChange={handleIgtChange}>
                    <option value="">-- Pilih Nama Informasi Geospasial Tematik --</option>
                    {fileTypes.map((type, index) => (
                      <option key={index} value={type.nama_igt}>{type.nama_igt}</option>
                    ))}
                  </select>
                </div>
                {(jenisKsp || kepkaData) && (
                  <div className="info-badge">
                    <Info size={16} />
                    <span>
                      {jenisKsp && <><strong>Jenis IGT:</strong> {jenisKsp}</>}
                      {kepkaData && kepkaData.jenis === 'Renaksi' && kepkaData.nama_igt_renaksi && (
                        <> | <strong>Nama IGT Renaksi:</strong> {kepkaData.nama_igt_renaksi}</>
                      )}
                      {kepkaData && kepkaData.jenis !== 'Renaksi' && kepkaData.nama_igt_walidata && (
                        <> | <strong>Nama IGT Walidata:</strong> {kepkaData.nama_igt_walidata}</>
                      )}
                      {kepkaData && kepkaData.jenis !== 'Renaksi' && kepkaData.nama_igt_renaksi && (
                        <> | <strong>Nama IGT Renaksi:</strong> {kepkaData.nama_igt_renaksi}</>
                      )}
                    </span>
                  </div>
                )}

                {!isLainnya && (
                  <div className="form-group">
                    <label className="form-label">Capaian <span className="required">*</span></label>

                    {isManualInput ? (
                      <>
                        <div className="capaian-item">
                          <div className="capaian-item-header">
                            <h4 className="capaian-title">Capaian Tahun Berjalan</h4>
                          </div>
                          <div className="capaian-row">
                            <div className="capaian-field">
                              <label>Tahun <span className="required">*</span></label>
                              <select
                                value={capaianList[0]?.tahun || new Date().getFullYear()}
                                onChange={(e) => updateCapaianTahun(0, 'tahun', e.target.value)}
                                className="form-input"
                                required
                              >
                                {Array.from({ length: 15 }, (_, i) => new Date().getFullYear() - 9 + i).map(year => (
                                  <option key={year} value={year}>
                                    {year}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="capaian-field">
                              <label>Nilai Capaian <span className="required">*</span></label>
                              <input
                                type="number"
                                value={capaianList[0]?.nilai || ''}
                                onChange={(e) => updateCapaianTahun(0, 'nilai', e.target.value)}
                                placeholder="Masukkan nilai"
                                required
                                min="0"
                              />
                            </div>
                            <div className="capaian-field">
                              <label>Target <span className="required">*</span></label>
                              <input
                                type="number"
                                value={capaianList[0]?.target || ''}
                                onChange={(e) => updateCapaianTahun(0, 'target', e.target.value)}
                                placeholder="Target"
                                required
                                min="0"
                              />
                            </div>
                            <div className="capaian-field">
                              <label>Satuan <span className="required">*</span></label>
                              <input
                                type="text"
                                value={capaianList[0]?.satuan || ''}
                                onChange={(e) => updateCapaianTahun(0, 'satuan', e.target.value)}
                                placeholder="Satuan"
                                required
                              />
                            </div>
                          </div>
                          {capaianList[0]?.nilai && capaianList[0]?.target && (
                            <div className="persentase-wrapper">
                              <span className={`persentase-badge ${parseFloat(capaianList[0].persentase || 0) >= 50 ? 'high' : 'low'}`}>
                                📊 Persentase: {capaianList[0].persentase || 0}%
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="future-target-section">
                          <h4 className="future-target-title">
                            📊 Informasi Target Capaian
                          </h4>
                          <div className="target-tahun-grid">
                            {Array.from(
                              { length: Math.max(0, 2029 - parseInt(capaianList[0]?.tahun || new Date().getFullYear())) },
                              (_, i) => parseInt(capaianList[0]?.tahun || new Date().getFullYear()) + 1 + i
                            ).map((year) => {
                              const yearData = futureYearsTarget.find(item => item.tahun === year) || { tahun: year, target: '', satuan: '' };
                              return (
                                <div key={year} className="target-tahun-card">
                                  <div className="target-tahun-card-title">
                                    Tahun {year}
                                  </div>
                                  <div className="target-tahun-field">
                                    <label>
                                      Target <span className="required">*</span>
                                    </label>
                                    <input
                                      type="number"
                                      value={yearData.target}
                                      onChange={(e) => {
                                        const newFutureYears = futureYearsTarget.filter(item => item.tahun !== year);
                                        newFutureYears.push({ tahun: year, target: e.target.value, satuan: yearData.satuan });
                                        setFutureYearsTarget(newFutureYears);
                                      }}
                                      placeholder="Target"
                                      required
                                      min="0"
                                    />
                                  </div>
                                  <div className="target-tahun-field">
                                    <label>
                                      Satuan <span className="required">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={yearData.satuan}
                                      onChange={(e) => {
                                        const newFutureYears = futureYearsTarget.filter(item => item.tahun !== year);
                                        newFutureYears.push({ tahun: year, target: yearData.target, satuan: e.target.value });
                                        setFutureYearsTarget(newFutureYears);
                                      }}
                                      placeholder="Satuan"
                                      required
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <small className="future-target-note">
                            ℹ️ Isi target dan satuan hingga tahun 2029. Nilai capaian hanya untuk tahun yang dipilih.
                          </small>
                        </div>
                      </>
                    ) : (
                      capaianList.map((item, index) => (
                        <div key={index} className="capaian-item">
                          <div className="capaian-item-header">
                            <h4 className="capaian-title">Capaian #{index + 1}</h4>
                            {capaianList.length > 1 && (
                              <button
                                type="button"
                                className="btn-remove-capaian"
                                onClick={() => removeCapaianTahun(index)}
                              >
                                <X size={14} /> Hapus
                              </button>
                            )}
                          </div>
                          <div className="capaian-row">
                            <div className="capaian-field">
                              <label>Tahun <span className="required">*</span></label>
                              <select
                                value={item.tahun}
                                onChange={(e) => updateCapaianTahun(index, 'tahun', e.target.value)}
                                className="form-input"
                                required
                              >
                                {Array.from({ length: 15 }, (_, i) => new Date().getFullYear() - 9 + i).map(year => {
                                  // Check if year is already used in other capaian items
                                  const isYearUsed = capaianList.some((cap, idx) => idx !== index && parseInt(cap.tahun) === year);
                                  return (
                                    <option key={year} value={year} disabled={isYearUsed}>
                                      {year} {isYearUsed ? '(sudah dipilih)' : ''}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            <div className="capaian-field">
                              <label>Nilai Capaian {(!isManualInput || (isManualInput && index === 0)) && <span className="required">*</span>}</label>
                              <input
                                type="number"
                                value={item.nilai}
                                onChange={(e) => updateCapaianTahun(index, 'nilai', e.target.value)}
                                placeholder={index > 0 && isManualInput ? "Tidak dapat diisi" : "Masukkan nilai"}
                                required={!isManualInput || (isManualInput && index === 0)}
                                disabled={isManualInput && index > 0}
                                min="0"
                              />
                            </div>
                            <div className="capaian-field">
                              <label>Target {isManualInput && <span className="required">*</span>}</label>
                              <input
                                type="number"
                                value={item.target}
                                onChange={(e) => updateCapaianTahun(index, 'target', e.target.value)}
                                placeholder="Target"
                                required={isManualInput}
                                disabled={!isManualInput}
                                min="0"
                              />
                            </div>
                            <div className="capaian-field">
                              <label>Satuan {isManualInput && <span className="required">*</span>}</label>
                              <input
                                type="text"
                                value={item.satuan}
                                onChange={(e) => updateCapaianTahun(index, 'satuan', e.target.value)}
                                placeholder="Satuan"
                                required={isManualInput}
                                disabled={!isManualInput}
                              />
                            </div>
                          </div>
                          {item.nilai && item.target && (
                            <div className="persentase-wrapper">
                              <span className={`persentase-badge ${parseFloat(item.persentase || 0) >= 50 ? 'high' : 'low'}`}>
                                📊 Persentase: {item.persentase || 0}%
                              </span>
                            </div>
                          )}
                        </div>
                      ))
                    )}

                    {!isManualInput && kepkaData && kepkaData.target && (
                      <div className="target-info-box">
                        <div className="target-info-title">
                          📊 Informasi Target Per Tahun:
                        </div>
                        <div className="target-info-grid">
                          {Object.entries(kepkaData.target).map(([year, target]) => (
                            <div key={year} className="target-info-item">
                              <strong>{year}:</strong> {target} {kepkaData.satuan}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {!isManualInput && (
                      <small className="form-text text-muted">
                        IGT sudah memiliki target. Isi nilai capaian untuk tahun berjalan. Lihat informasi target per tahun di atas.
                      </small>
                    )}
                  </div>
                )}

                {kepkaData?.jenis === 'Renaksi' && (
                  <div className="form-group">
                    <label htmlFor="cakupan" className="form-label">
                      Catatan <span className="required">*</span>
                    </label>
                    <textarea
                      className="form-textarea"
                      id="cakupan"
                      rows="5"
                      required
                      value={cakupan}
                      onChange={handleCakupanChange}
                      placeholder="Masukkan catatan terkait data Renaksi anda..."
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="files-section">
              <h3
                className={`section-title ${kepkaData?.jenis === 'Renaksi' ? 'clickable' : ''}`}
                onClick={() => kepkaData?.jenis === 'Renaksi' && setIsDocsExpanded(!isDocsExpanded)}
                style={kepkaData?.jenis === 'Renaksi' ? { cursor: 'pointer' } : {}}
              >
                {kepkaData?.jenis === 'Renaksi' ? (
                  <><FilePlus size={22} /> Dokumen Kelengkapan <span className="optional-badge">(Opsional)</span> {isDocsExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</>
                ) : (
                  <><FileIcon size={22} /> Dokumen Kelengkapan</>
                )}
              </h3>
              {(kepkaData?.jenis !== 'Renaksi' || isDocsExpanded) && <div className="form-card">
                {uploadFields.map((input, docIndex) => {
                  const documentChecklist = checklistData.filter(item => item.document_type === input.document_type);
                  const hasChecklist = documentChecklist.length > 0;
                  const docNumber = docIndex + 1;
                  const isRenaksiType = kepkaData?.jenis === 'Renaksi';
                  const isFieldRequired = input.required && !isRenaksiType;

                  return (
                    <div key={input.id}>
                      <div className="form-group">
                        <div className="form-label-with-download">
                          <label className="form-label" htmlFor={input.id}>
                            {docNumber}. {input.label} {isFieldRequired && <span className="required">*</span>}
                          </label>
                          {templateMapping[input.id] && (
                            <button
                              type="button"
                              className="btn-download-template"
                              onClick={() => downloadTemplate(input.id)}
                              disabled={downloadLoading[input.id]}
                              title={`Unduh template ${input.label}`}
                            >
                              <Download size={14} />
                              {downloadLoading[input.id] ? 'Mengunduh...' : 'Unduh Template'}
                            </button>
                          )}
                        </div>

                        {(() => {
                          const docTypeMapping = {
                            file1: 'struktur_data',
                            file2: 'takel',
                            file5: 'simbology',
                            file6: 'role'
                          };

                          const docType = docTypeMapping[input.id];
                          if (!docType) return null;

                          const hasLatestDoc = latestDocuments[docType];
                          const isSynced = syncedDocuments[docType];

                          if (!hasLatestDoc) return null;

                          const docLabels = {
                            struktur_data: 'Struktur Data',
                            takel: 'Mekanisme dan Tata Kerja',
                            simbology: 'Simbology',
                            role: 'Role Akses'
                          };

                          return (
                            <div className={`sync-banner ${isSynced ? 'synced' : 'not-synced'}`}>
                              <div className="sync-banner-content">
                                <div className="sync-banner-text">
                                  <label className={`sync-banner-label ${isSynced ? 'synced' : 'not-synced'}`}>
                                    {isSynced ? '🔄' : '⚡'} Pakai data {docLabels[docType]} yang sudah terintegrasi ?
                                  </label>
                                  <small className="sync-banner-description">
                                    {isSynced
                                      ? `Tersinkronisasi dari nomer tiket ${syncedDocuments[docType].no_tiket}`
                                      : `Data sebelumnya Sudah Terintegrasi dengan nama dokumen : ${hasLatestDoc.filename}`
                                    }
                                  </small>
                                </div>
                                {isSynced ? (
                                  <button
                                    type="button"
                                    onClick={() => handleUnsyncDocument(docType)}
                                    className="btn-sync unsync"
                                  >
                                    ❌ Batal Sinkronisasi
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleSyncDocument(docType)}
                                    className="btn-sync sync"
                                  >
                                    🔄 Sinkronisasi
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        <div className={`file-input-wrapper ${fileErrors[input.id] ? 'has-error' : ''} ${(input.id === 'file1' && syncedDocuments.struktur_data) ||
                          (input.id === 'file2' && syncedDocuments.takel) ||
                          (input.id === 'file5' && syncedDocuments.simbology) ||
                          (input.id === 'file6' && syncedDocuments.role)
                          ? 'disabled' : ''}`}>
                          <div className="file-input-icon"><UploadCloud size={20} /></div>
                          <input
                            type="file"
                            className="file-input-native"
                            id={input.id}
                            onChange={handleFileChange}
                            accept={input.accept}
                            disabled={(
                              (input.id === 'file1' && syncedDocuments.struktur_data) ||
                              (input.id === 'file2' && syncedDocuments.takel) ||
                              (input.id === 'file5' && syncedDocuments.simbology) ||
                              (input.id === 'file6' && syncedDocuments.role)
                            )}
                          />
                          <span className="file-input-label">
                            {fileNames[input.id] || 'Klik untuk memilih file...'}
                          </span>
                        </div>
                        <small className="form-text text-muted">Format yang diizinkan: {input.accept}</small>
                        {fileErrors[input.id] && (
                          <div className="error-message"><AlertTriangle size={14} /> {fileErrors[input.id]}</div>
                        )}
                        {fileNames[input.id] && !fileErrors[input.id] && (
                          <div className="success-message"><CheckCircle size={14} /> File dipilih: {fileNames[input.id]}</div>
                        )}

                        {input.id === 'file4' && files.file4 && files.file4.name.toLowerCase().endsWith('.xml') && (
                          <button
                            type="button"
                            onClick={handleOpenMetadataEditor}
                            className="btn-metadata-editor"
                          >
                            <Edit3 size={16} />
                            Edit Metadata dengan Form Editor
                          </button>
                        )}
                      </div>

                      {(() => {
                        const docTypeMapping = {
                          file1: 'struktur_data',
                          file2: 'takel',
                          file5: 'simbology',
                          file6: 'role'
                        };

                        const docType = docTypeMapping[input.id];
                        const hasFile = files[input.id];
                        const isSynced = docType && syncedDocuments[docType];
                        const hasFileOrSync = hasFile || isSynced;

                        return hasChecklist && hasFileOrSync && (
                          <div className="checklist-section">
                            <div
                              className={`checklist-header ${expandedChecklists[input.document_type] ? 'expanded' : ''}`}
                              onClick={() => setExpandedChecklists(prev => ({
                                ...prev,
                                [input.document_type]: !prev[input.document_type]
                              }))}
                            >
                              <span className="checklist-header-title">
                                12 Elemen SPD melengkapi deputi IIG No.4 tahun 2025 tentang format standar data geospasial.
                              </span>
                              <span className="checklist-header-icon">
                                {expandedChecklists[input.document_type] ? '▲' : '▼'}
                              </span>
                            </div>

                            {expandedChecklists[input.document_type] && (
                              <div className="checklist-items">
                                {documentChecklist
                                  .sort((a, b) => {
                                    if (a.priority === 'mandatory' && b.priority === 'optional') return -1;
                                    if (a.priority === 'optional' && b.priority === 'mandatory') return 1;
                                    return 0;
                                  })
                                  .map((item, idx) => (
                                    <div key={item.id} className={`checklist-item ${idx < documentChecklist.length - 1 ? '' : 'last'}`}>
                                      <div className="checklist-item-label">
                                        {item.description}
                                      </div>
                                      <textarea
                                        className="form-input checklist-input"
                                        value={checklistInputs[item.id] || ''}
                                        onChange={(e) => setChecklistInputs(prev => ({
                                          ...prev,
                                          [item.id]: e.target.value
                                        }))}
                                        placeholder={`Masukkan informasi untuk ${item.description}`}
                                        rows={3}
                                      />
                                      {item.input_hint ? (
                                        <small className="checklist-hint">
                                          {item.input_hint}
                                        </small>
                                      ) : (
                                        <small className="checklist-hint-empty">-</small>
                                      )}
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}

                {kepkaData?.jenis !== 'Renaksi' && (
                  <div className="form-group">
                    <label>9. Cakupan *</label>
                    <textarea
                      className="form-textarea"
                      value={cakupan}
                      onChange={(e) => setCakupan(e.target.value)}
                      placeholder="Masukkan cakupan wilayah data IGT"
                      rows={3}
                    />
                  </div>
                )}
              </div>}
            </div>

            <div className="submit-section">
              {uploadProgress > 0 && (
                <div className="progress-wrapper">
                  <ProgressBar
                    now={uploadProgress}
                    label={`${uploadProgress}%`}
                    animated
                    variant="success"
                    className="progress-bar-styled"
                  />
                </div>
              )}
              <div className="submit-section-buttons">
                <button
                  type="button"
                  className="btn-save-draft"
                  onClick={handleSaveDraft}
                  disabled={loading}
                >
                  <Save size={16} />
                  Simpan Draft
                </button>
                <button
                  type="submit"
                  className="btn-submit btn-submit-styled"
                  disabled={loading || Object.keys(fileErrors).length > 0}
                >
                  <Send size={16} />
                  {loading ? 'Mengunggah...' : 'Upload'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UploadPage;